/*
  TOSSIT cart drawer
  ==================
  The replacement for AMP's cart. Renders from /cart.js rather than from
  section HTML, because the rewards bar and the offer need the cart totals as
  numbers anyway - once you are holding the JSON, asking Shopify to re-render
  markup as well is a second round trip for the same information.

  The one rule that governs this file: THE THEME NEVER DECIDES MONEY.

  Every reward is a Shopify automatic discount. Verified against the live
  store: a poster added to an $89.97 cart came back at $0.00 with no code
  applied, and the same poster in a $37.98 cart stayed $7.99. So Shopify
  enforces the thresholds, and everything here is presentation. If the tier
  amounts in theme settings ever disagree with the real discounts, the
  customer sees the wrong encouragement - they never get the wrong price.

  The free gift is CLAIMED, never injected. Crossing a gift tier reveals an
  ADD button; the customer presses it. Auto-adding would put an item in
  someone's cart that they did not choose, and would then need rules for
  re-removing it, for not re-adding what they deleted, and for what happens
  when the total drops back under. None of that exists because the customer
  is the one who decides.
*/

(function () {
  var root = document.querySelector('[data-tossit-cart]');
  var configEl = document.querySelector('[data-tossit-cart-config]');
  if (!root || !configEl) return;

  var CFG;
  try {
    CFG = JSON.parse(configEl.textContent);
  } catch (e) {
    return; // Malformed config: leave the theme's own cart alone entirely.
  }

  var tiers = (CFG.tiers || []).slice().sort(function (a, b) { return a.amount - b.amount; });

  // How many upsell cards the cart shows beneath the line items. Two suggests
  // a choice rather than a single push, without turning the drawer into a
  // catalogue - and the claimable gift can already sit above these.
  var OFFER_COUNT = 2;

  var els = {
    panel: root.querySelector('.tcart__panel'),
    count: root.querySelector('[data-tcart-count]'),
    rewards: root.querySelector('[data-tcart-rewards]'),
    lines: root.querySelector('[data-tcart-lines]'),
    empty: root.querySelector('[data-tcart-empty]'),
    offer: root.querySelector('[data-tcart-offer]'),
    foot: root.querySelector('[data-tcart-foot]'),
    totals: root.querySelector('[data-tcart-totals]'),
    discountForm: root.querySelector('[data-tcart-discount-form]'),
    discountMsg: root.querySelector('[data-tcart-discount-msg]'),
    discountWrap: root.querySelector('[data-tcart-discount-wrap]'),
    walletScroll: root.querySelector('[data-tcart-wallets]')
  };

  var lastFocus = null;
  var busy = false;
  var cart = null;

  /*
     The add-to-cart theatre, borrowed rather than reimplemented.

     Identical markup and keyframes to the main buy button (.th-atc-* in
     tossit-header.js / tossit-header.css) - cart rolls in, dart drops, lands
     in the basket. Those classes are unscoped so they work anywhere, and
     sharing them means the two buttons cannot drift apart.

     It reads differently here only because this button is green: the cart and
     dart are drawn in currentColor, which is house ink on green.
  */
  var ATC_STAGE_HTML =
    '<span class="th-atc-stage" aria-hidden="true">' +
    '<span class="th-atc-roll">' +
    '<svg class="th-atc-cart" viewBox="0 0 36 26" fill="none">' +
    '<polyline points="1 2.5 6 2.5 10 18.5 25.5 18.5 28.5 7.5 7.5 7.5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>' +
    '<svg class="th-atc-dart" viewBox="0 0 24 62">' +
    '<path fill="currentColor" d="M12 1 C18 1 22 5 22 10 C22 15 17 17 15 22 L14 30 L14 40 L16 42 L18 44 L18 50 L15 52 L14 54 C17 55 20 57 22 59 L22 61 L2 61 L2 59 C4 57 7 55 10 54 L9 52 L6 50 L6 44 L8 42 L10 40 L10 30 L9 22 C7 17 2 15 2 10 C2 5 6 1 12 1 Z"/>' +
    '</svg>' +
    '</span>' +
    '</span>';

  /*
     Stand Dawn's own cart UI down, without removing it.

     This store's cart_type is 'notification', so sections/header.liquid
     renders <cart-notification>. Mounting this drawer does not replace it -
     layout/theme.liquid only swaps out Dawn's cart-DRAWER - so every add
     popped Dawn's notification AND slid this drawer over it: two panels, two
     subtotals, two checkout buttons, two focus traps.

     The obvious fix is to stop rendering the element. That is worse.
     product-form.js:11 looks for <cart-notification> or <cart-drawer>, and
     :70 redirects the whole page to /cart when it finds neither - so
     deleting it trades a double drawer for a full page load on every add.

     Keep the element, silence it. open() is what adds .active and starts
     Dawn's focus trap; shadowing it on the instance leaves product-form's
     lookup satisfied and its section re-rendering intact, while nothing of
     Dawn's ever appears.
  */
  (function silenceDawnCart() {
    var note = document.querySelector('cart-notification');
    if (!note) return;
    note.open = function () {};
    if (note.notification && note.notification.classList) {
      note.notification.classList.remove('animate', 'active');
    }
  })();

  // §1 — money. Shopify's money_format is a template, not a locale.

  function money(cents) {
    var v = (cents / 100).toFixed(2);
    var parts = v.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var amount = parts.join('.');
    var fmt = CFG.moneyFormat || '${{amount}}';
    // Only the plain and comma-separated forms appear in practice; anything
    // exotic still yields a readable number rather than a broken token.
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/g, amount).replace(/<[^>]+>/g, '');
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // §2 — cart transport

  // Rejects on a non-2xx rather than parsing whatever came back. Shopify
  // answers an error with a JSON body too, and assigning that to `cart` used
  // to paint a drawer built from an error object.
  function getCart() {
    return fetch(CFG.cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart ' + r.status);
        return r.json();
      });
  }

  /*
     What the reward thresholds are measured against.

     NOT cart.total_price. That is the total AFTER discounts, so the moment
     any discount landed the bar un-reached tiers the customer had already
     earned, and ran BACKWARDS as they added more product - the opposite of
     what a progress bar promises.

     items_subtotal_price is the subtotal after line-level discounts, which
     is both what Shopify evaluates automatic discounts against and the
     figure that behaves correctly here: the claimed free gift sits at zero
     so it cannot inflate the basis toward earning itself, and an order-level
     discount cannot pull the bar back down.
  */
  function rewardBasis() {
    if (typeof cart.items_subtotal_price === 'number') return cart.items_subtotal_price;
    return cart.total_price;
  }

  function say(message, bad) {
    if (!els.discountMsg) return;
    els.discountMsg.hidden = false;
    els.discountMsg.textContent = message;
    els.discountMsg.classList.toggle('is-bad', !!bad);
  }

  /*
     Released on EVERY outcome, including the one where both the request and
     its fallback reject.

     This used to clear `busy` in a trailing .then() with no rejection
     handler. One flaky moment on mobile and the rejection sailed past the
     cleanup, so `busy` stayed true for the life of the page - and
     .tcart.is-busy puts `pointer-events: none` on the whole panel. Checkout,
     quantity, remove, Apply and the close button all went dead, and stayed
     dead when the connection came back. The customer's only way out was a
     reload they had no reason to think would help.
  */
  function release(c) {
    busy = false;
    root.classList.remove('is-busy');
    if (c) {
      document.dispatchEvent(new CustomEvent('tossit:cart:updated', { detail: c }));
      // Nothing in the theme listens for the event above. The header owns the
      // count bubble and listens for 'cart:refresh' (tossit-header.js), so
      // without this the icon kept its old number after every change made in
      // here - including showing "1" over an emptied cart until a page load.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
    /*
       The gift revoke has to run HERE, after busy drops - not only in
       render(). Every mutation renders while its own request still holds
       `busy`, so a revoke checked only at render time would no-op on the
       exact path that matters: the removal that dropped the basis below
       the tier. Runs on every outcome; once the gift line is gone it is a
       single cheap comparison.
    */
    /*
       Only chase the gift when the request we are releasing actually
       WORKED. Without this it was an unbounded request loop: enforceGift ->
       change -> the POST 429s (Shopify's cart bucket) -> the fallback
       getCart also fails -> release(null) -> enforceGift -> change again.
       Two failed requests per turn, sustaining the very rate limit it was
       reacting to, and termination required the success that was failing.
    */
    if (c) enforceGift();
    return c;
  }

  /*
     Losing a bundle discount should never be a surprise at checkout.

     Shopify already handles the money correctly: automatic discounts are
     re-evaluated on every cart change, so removing the base item of a
     Buy-X-Get-Y drops the discount server-side without the theme doing
     anything. Nothing here enforces that, and nothing here should.

     What was missing was being TOLD. Someone removes a starter pack and the
     saving on the expansion silently evaporates, discovered - if at all - as
     a larger number at checkout.

     So this reports what Shopify actually did, after the fact. It does not
     predict, because predicting means the theme deciding money and being
     wrong the moment a discount is edited in admin. It compares
     total_discount before and after, and if the saving really did shrink it
     says so and offers to put the line back.
  */
  var undoLine = null;

  function watchDiscount(before, removed) {
    /*
       Clear the last warning first.

       Nothing used to retract this. "That removed $12.08 of discount. Undo?"
       was written once and then sat above Checkout for the rest of the
       session - through re-adds, quantity changes, everything - until the
       drawer was reloaded. It ended up contradicting the very totals printed
       under it, and its "Put it back" button stayed live against a line the
       customer may have already restored by hand, so pressing it added a
       second copy. A warning about a state that has passed is worse than
       silence: it teaches people not to read the messages that matter.
    */
    if (!cart || cart.total_discount >= before) {
      undoLine = null;
      if (els.discountMsg) {
        els.discountMsg.hidden = true;
        els.discountMsg.textContent = '';
        els.discountMsg.classList.remove('is-bad');
      }
      return;
    }
    var lost = before - cart.total_discount;
    undoLine = removed || null;
    say(
      'That removed ' + money(lost) + ' of discount.' + (undoLine ? ' Undo?' : ''),
      true
    );
    if (undoLine && els.discountMsg) {
      els.discountMsg.insertAdjacentHTML(
        'beforeend',
        ' <button type="button" class="tcart__undo" data-tcart-undo>Put it back</button>'
      );
    }
  }

  /*
     Addressed by Shopify's line KEY, never by position.

     /cart/change.js accepts either, and this used to send `line: 3`. A cart
     position is only true for as long as nothing else touches the cart - and
     something else does: a second tab, the /cart page, the product-page box,
     the exit popup, or the server itself dropping a sold-out line. Between
     this drawer's last render and the press, position 3 can become a
     different product, and the customer's Remove then deletes something they
     did not choose. The key is bound to the line item itself, so a stale
     position can only fail loudly (Shopify 404s an unknown key) instead of
     quietly hitting the wrong product.
  */
  function change(key, quantity) {
    if (busy) return Promise.resolve(cart);

    // Captured before the request, because afterwards the line is gone.
    var discountBefore = cart ? cart.total_discount : 0;
    var removed = null;
    if (quantity === 0 && cart && cart.items) {
      var it = cart.items.filter(function (i) { return i.key === key; })[0];
      if (it) removed = { id: it.variant_id, quantity: it.quantity, title: it.product_title };
    }

    busy = true;
    root.classList.add('is-busy');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        // A 422 (usually an inventory limit) still returns JSON. Rendering
        // that body as if it were a cart painted a corrupted drawer for a
        // whole round trip before reverting, with nothing said to anyone.
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (err) {
            throw new Error(err.description || err.message || 'Could not update that.');
          });
        }
        return r.json();
      })
      .then(function (c) { cart = c; giftFixTries = 0; render(); watchDiscount(discountBefore, removed); return c; })
      .catch(function (err) {
        say(err && err.message ? err.message : 'Could not update that.', true);
        // Re-sync from the server so the drawer shows what is really there.
        return getCart().then(function (c) { cart = c; render(); return c; });
      })
      .then(release)
      .catch(function () { return release(null); });
  }

  function add(id, quantity) {
    if (busy) return Promise.resolve(cart);
    busy = true;
    root.classList.add('is-busy');
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: quantity || 1 }] })
    })
      .then(function (r) {
        // The response was previously discarded entirely, so a sold-out
        // variant or an inventory ceiling read as success: the button
        // re-enabled, the offer card stayed put, and nothing anywhere told
        // the customer the item had not gone in.
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (err) {
            throw new Error(err.description || err.message || 'That item could not be added.');
          });
        }
        return r.json();
      })
      .then(function () { return getCart(); })
      .then(function (c) { cart = c; render(); return c; })
      .catch(function (err) {
        say(err && err.message ? err.message : 'That item could not be added.', true);
        return getCart().then(function (c) { cart = c; render(); return c; }).catch(function () { return null; });
      })
      .then(release)
      .catch(function () { return release(null); });
  }

  // §3 — rewards bar
  //
  // Progress is measured against the LAST tier, so the indicator reaches the
  // end exactly when every reward is unlocked. Each tier sits at its true
  // proportional position rather than at an even interval, so the gaps tell
  // the truth about how far apart the thresholds are.

  // One segment per reward, each the same width, each filling on its own.
  //
  // This was a single track with markers positioned absolutely along it, and
  // it never sat right. With markers at even intervals the left third held no
  // marker at all - dead space before the first reward - while the last one
  // pinned to 100% hung off the right edge and needed its label right-aligned
  // as a special case. Centred labels next to one right-aligned label is what
  // read as badly justified.
  //
  // Equal segments remove the whole problem rather than patch it: no absolute
  // positioning, no first/last special cases, no labels to collide. Every
  // reward owns an identical band with its label centred beneath, so the row
  // is symmetric at any number of tiers.
  //
  // Fill is per-segment, so a cart one cent short of a threshold can never
  // paint into the next band.
  function segmentFill(total, i) {
    var floor = i === 0 ? 0 : tiers[i - 1].amount;
    var span = tiers[i].amount - floor;
    if (span <= 0) return total >= tiers[i].amount ? 100 : 0;
    return Math.max(0, Math.min(1, (total - floor) / span)) * 100;
  }

  var previouslyReached = null;
  // True for exactly one render: the one where the top reward lands.
  var ignite = false;

  // Which lines were discounted at the last paint, so a price that newly
  // becomes discounted can be marked exactly once. Keyed by Shopify's line key.
  var previousDiscount = {};
  var nextDiscount = {};

  function renderRewards() {
    if (!tiers.length) { els.rewards.hidden = true; return; }
    els.rewards.hidden = false;

    var total = rewardBasis();

    var next = null;
    for (var i = 0; i < tiers.length; i++) {
      if (total < tiers[i].amount) { next = tiers[i]; break; }
    }

    var reachedNow = tiers.map(function (t) { return total >= t.amount; });

    var segments = tiers.map(function (t, i) {
      var reached = reachedNow[i];
      // Only celebrate a tier the moment it flips, and never on first paint -
      // arriving at a cart that already qualifies is not an achievement.
      var justReached = reached && previouslyReached && !previouslyReached[i];
      var pct = segmentFill(total, i);
      var active = !reached && pct > 0;

      /*
         Each tier wears its own medallion - a drawn icon of the PRIZE, not
         an abstract dot: the rolling cart for free shipping, the "?" for
         the mystery poster, a % for the discount. Unreached it sits in
         outline; reached it flips to the check (with the existing pop).
         And the ACTIVE segment's fill is led by the house dart, so the bar
         reads as a dart mid-flight toward the next target rather than a
         thermometer. All of it is drawing - the numbers never moved.
      */
      var icon =
        t.kind === 'shipping'
          ? '<svg viewBox="0 0 36 26" width="13" height="10"><polyline points="1 2.5 6 2.5 10 18.5 25.5 18.5 28.5 7.5 7.5 7.5" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          : t.kind === 'gift'
            ? '<svg viewBox="0 0 16 16" width="11" height="11"><text x="8" y="12.6" text-anchor="middle" font-size="13" font-weight="900" font-family="inherit" fill="currentColor">?</text></svg>'
            : '<svg viewBox="0 0 16 16" width="11" height="11"><text x="8" y="12.6" text-anchor="middle" font-size="11.5" font-weight="900" font-family="inherit" fill="currentColor">%</text></svg>';


      /*
         Proximity, handed to CSS as a 0-1 number. The glow, its spread and
         the pulse all scale off it, so the bar gets visibly hotter the
         closer the next reward is - the last few dollars should FEEL like
         the last few dollars. is-close crosses at three quarters, where
         the encouragement becomes worth pressing.
      */
      var near = active ? Math.max(0, Math.min(1, pct / 100)) : 0;

      return (
        '<li class="tcart__seg tcart__seg--' + esc(t.kind || 'tier') +
          (reached ? ' is-reached' : '') +
          (justReached ? ' is-just-reached' : '') +
          (active ? ' is-active' : '') +
          (near >= 0.75 ? ' is-close' : '') + '"' +
          (active ? ' style="--tc-near:' + near.toFixed(2) + '"' : '') + '>' +
          '<span class="tcart__seg-bar" aria-hidden="true">' +
            '<i class="tcart__seg-fill" style="width:' + pct + '%"></i>' +
          '</span>' +
          '<span class="tcart__seg-labels">' +
            '<span class="tcart__seg-amount">' +
              '<span class="tcart__seg-medal" aria-hidden="true">' +
                '<span class="tcart__seg-icon">' + icon + '</span>' +
                '<span class="tcart__seg-check">' +
                  '<svg viewBox="0 0 16 16" width="9" height="9"><path d="M2 8.5 L6 12.5 L14 3.5" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
                '</span>' +
              '</span>' +
              esc(money(t.amount)) +
            '</span>' +
            '<span class="tcart__seg-label">' + esc(t.label) + '</span>' +
          '</span>' +
        '</li>'
      );
    }).join('');

    var message = next
      ? '<strong>' + esc(money(next.amount - total)) + '</strong> away from ' + esc(next.label)
      /*
         Name the top reward rather than saying "everything unlocked".

         The biggest discount in the store is the one worth stating plainly at
         the moment it applies - "33% off" tells the customer what they got,
         where a generic line makes them go looking for it on the totals.
      */
      : (tiers.length ? esc(tiers[tiers.length - 1].label) + ' unlocked \u2014 applied at checkout'
                      : 'Every reward unlocked.');

    els.rewards.innerHTML =
      '<p class="tcart__rewards-msg" role="status">' + message + '</p>' +
      '<ol class="tcart__track' + (next ? '' : ' is-complete') + '" role="list">' + segments + '</ol>';

    /*
       The top tier is the one worth burning the house down for. When it
       flips - and only on the render where it flips - the totals ignite:
       the number the customer has been watching climb finally goes DOWN,
       and that is the moment to point at it.

       Set here rather than in renderTotals because this is where the
       before/after is known; renderTotals runs after and consumes it.
    */
    var topIdx = tiers.length - 1;
    ignite = topIdx >= 0 && reachedNow[topIdx] && previouslyReached && !previouslyReached[topIdx];

    previouslyReached = reachedNow;
  }

  // §4 — the claimable gift
  //
  // Shown only once its tier is reached and only while the gift is not
  // already in the cart. Pressing ADD adds it at full price; Shopify's
  // automatic discount is what makes it free, which is why the button
  // promises "free" only when the threshold is genuinely met.

  function giftTier() {
    for (var i = 0; i < tiers.length; i++) if (tiers[i].kind === 'gift') return tiers[i];
    return null;
  }

  /*
     The gap-filler: the most personal card the drawer can show.

     The rewards bar already tells the customer they are "$8.48 away from
     Free poster" - and then leaves them to work out what $8.48 buys. This
     card answers it: the CHEAPEST catalogue item that single-handedly
     crosses the next tier, headed with what it unlocks. It is arithmetic on
     the customer's own cart, so no two carts see the same card, and the
     claim is honest by construction - the tier thresholds and prices are
     Shopify's, the theme just did the subtraction. The reward itself is
     still granted (or not) server-side; the card only points.

     Never the gift product itself: selling someone the poster they are
     about to earn free is the one suggestion worse than none.

     Cheapest-that-crosses, not closest: minimising the ask maximises the
     chance they take it, and any crossing item lights the same tier.
  */
  function tierGapOffer() {
    if (!window.TossitOffers || !CFG.upsell || !CFG.upsell.catalog) return null;
    var basis = rewardBasis();
    if (!basis) return null; // empty cart: there is no gap to finish
    var next = null;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].amount > basis) { next = tiers[i]; break; }
    }
    if (!next) return null; // every reward already earned
    var gapAmt = next.amount - basis;

    var idx = window.TossitOffers.indexCart(cart);
    var cat = CFG.upsell.catalog;

    /*
       The gap-filler was category-blind: a poster-only cart got offered a
       SECOND poster, because cheapest-that-crosses is all it asked. But the
       rules already know what a dartless cart needs, so the cart's own
       matching rule gets first refusal on the category - "Your poster needs
       darts" is both the right pitch and a tier-crosser. Falls back to the
       whole catalogue when no rule matches or its category has nothing left.
    */
    var preferIds = null;
    if (window.TossitOffers.categoriesInCart) {
      var present = window.TossitOffers.categoriesInCart(CFG.upsell, idx.products);
      var rules = CFG.upsell.rules || [];
      for (var r = 0; r < rules.length; r++) {
        var rule = rules[r];
        var hasAll = (rule.has || []).every(function (k) { return present[k]; });
        var lacksAll = (rule.lacks || []).every(function (k) { return !present[k]; });
        if (hasAll && lacksAll && rule.offer) {
          var ids = (CFG.upsell.categories || {})[rule.offer] || [];
          if (ids.length) { preferIds = {}; ids.forEach(function (x) { preferIds[String(x)] = true; }); }
          break;
        }
      }
    }

    function cheapestCrossing(restrict) {
      var b = null, bid = null;
      Object.keys(cat).forEach(function (id) {
        var p = cat[id];
        if (!p || (p.price || 0) < gapAmt) return; // cannot cross the line alone
        if (restrict && !restrict[String(id)]) return;
        if (idx.products[Number(id)] || idx.variants[Number(p.variantId)]) return;
        if (CFG.gift && Number(p.variantId) === Number(CFG.gift.id)) return;
        // The shared gate. This card is the FIRST thing the strip shows, and
        // it used to be the one picker that answered to nobody.
        if (window.TossitOffers.offerable &&
            !window.TossitOffers.offerable(CFG.upsell, id, p, cart, {
              intent: window.TossitIntent ? window.TossitIntent.profile() : null
            })) return;
        if (!b || p.price < b.price) { b = p; bid = id; }
      });
      return b ? { p: b, id: bid } : null;
    }

    var hit = (preferIds && cheapestCrossing(preferIds)) || cheapestCrossing(null);
    if (!hit) return null;
    var best = hit.p, bestId = hit.id;

    /*
       The sentence that closes a cart sitting just under a percentage tier:
       adding something can make the total go DOWN. Only sayable when the
       merchant has told us the tier's real percent (tossit_cart_tier_3_percent,
       documented as mirroring the admin discount) - without it the drawer
       says "Unlocks 22% off" and invents nothing.
    */
    /*
       Name the tier the PICK crosses, not the one that was nearest before
       a product was chosen. `next` is locked in before any candidate
       exists, so a $16.99 expansion offered to close a $9.01 gap to free
       shipping was sold as "Unlocks Free shipping" when it actually
       carried the cart to the Mystery poster. The biggest ask in the strip
       was advertised on the smallest reward in the ladder.
    */
    var crossed = next;
    for (var ti = 0; ti < tiers.length; ti++) {
      var tt = tiers[ti];
      if (tt.amount > basis && tt.amount <= basis + best.price) crossed = tt;
    }
    var heading = 'Unlocks ' + crossed.label;
    if (CFG.tierPercent > 0 && Number(CFG.tierPercentAmount) === Number(crossed.amount)) {
      var after = basis + best.price;
      var charged = Math.round(after * (100 - CFG.tierPercent) / 100);
      if (charged < basis) {
        heading = 'Add this and your total DROPS ' + money(basis - charged);
      }
    }

    // The gap card rotates colour like every engine pick - same seed
    // convention (the cart's first line), same picture-follows-colour rule.
    var gv = window.TossitOffers.offerVariant
      ? window.TossitOffers.offerVariant(best, (cart.items[0] && cart.items[0].product_id) || 0)
      : null;
    return {
      id: gv ? gv.id : best.variantId, productId: bestId, title: best.title,
      price: best.price, compareAt: best.compareAt,
      image: (gv && gv.image) || best.image, url: best.url,
      heading: heading,
      source: 'tier-gap'
    };
  }

  function catIdSet(keys) {
    var s = {};
    var cats = (CFG.upsell && CFG.upsell.categories) || {};
    (keys || []).forEach(function (k) {
      (cats[k] || []).forEach(function (id) { s[String(id)] = true; });
    });
    return s;
  }

  /*
     The one-away nudge: the single most persuasive true sentence this cart
     can be told.

     A cart holding a darts item plus exactly ONE add-on is one add-on short
     of the bundle discount - and the customer almost certainly does not
     know that the next add-on turns 27% on for BOTH of them, which often
     makes the second one nearly free. Detecting the state is arithmetic on
     the cart against the same bundle config the product box reads, so the
     claim can never drift from the discount it describes.

     Quantities, not lines: two of the same poster count as two gets, which
     is how the server counts them. Stands down past the supersede threshold
     - up there the 33% tier owns the messaging and quoting 27% would be
     advertising the smaller number.
  */
  /*
     The pack ladder, in the cart itself.

     The exit popup has argued this for weeks: a Starter is really half a
     Family Pack, a Family half a Party. But the popup only speaks to
     people who are LEAVING - the drawer talks to everyone who opens their
     cart. Same ladder config, same whole-ladder guard (a cart holding a
     Party Pack is never offered the Family), same maths, same
     add-then-remove swap - ported, not reinvented, so the two can never
     disagree about what an upgrade costs.
  */
  function findUpgrade() {
    var tree = CFG.upsell || {};
    var ladders = tree.ladders || {};
    var catalog = tree.catalog || {};
    if (!cart || !cart.items) return null;
    var have = {};
    cart.items.forEach(function (i) { have[String(i.product_id)] = true; });

    for (var key in ladders) {
      if (!Object.prototype.hasOwnProperty.call(ladders, key)) continue;
      var rungs = ladders[key] || [];
      for (var r = 0; r < rungs.length - 1; r++) {
        var here = (rungs[r] && rungs[r].ids) || [];
        for (var c = 0; c < cart.items.length; c++) {
          var line = cart.items[c];
          if (here.indexOf(Number(line.product_id)) === -1) continue;

          // The whole ladder above, not just the next rung - the popup
          // learned this the hard way.
          var ownsBetter = false;
          for (var hi = r + 1; hi < rungs.length && !ownsBetter; hi++) {
            ((rungs[hi] && rungs[hi].ids) || []).forEach(function (cand) {
              if (have[String(cand)]) ownsBetter = true;
            });
          }
          if (ownsBetter) continue;

          for (var up = r + 1; up < rungs.length; up++) {
            var candidates = (rungs[up] && rungs[up].ids) || [];
            for (var n = 0; n < candidates.length; n++) {
              var id = String(candidates[n]);
              var p = catalog[id];
              if (!p || !p.variants || p.price <= line.price) continue;
              var avail = (p.variants || []).some(function (v) { return v.available; });
              if (!avail) continue;
              /*
                   Single-quantity lines only.

                   diff is a UNIT difference but the swap moves the whole
                   line, so on a 2x Starter cart the card read "$25.00 more"
                   and the press charged $50.00 - and "4 players instead of
                   2" was nonsense to someone who already had four. Every
                   number would need a different multiplier and the value
                   case stops making sense, so a multi-quantity cart falls
                   through to the engine rather than being sold a lie.
              */
              if ((line.quantity || 1) !== 1) continue;
              return {
                from: line,
                to: p,
                toProductId: id,
                fromPlayers: (rungs[r] && rungs[r].players) || 0,
                toPlayers: (rungs[up] && rungs[up].players) || 0,
                diff: p.price - line.price
              };
            }
          }
        }
      }
    }
    return null;
  }

  /*
     The LUMO cross-sell: one lamp, its own titled shelf, at the foot of the
     offer strip.

     This is the sanctioned door through the brand fence. The fence exists
     because an UNLABELED lamp among dart suggestions reads as a glitch - a
     titled "for after the game" shelf reads as a second range, which is
     what it is. It stands down the moment the cart already speaks LUMO
     (the engine's own lamp offers take over), and honours dismissals like
     every other card.
  */
  function lumoCrossSell(onStrip) {
    var tree = CFG.upsell || {};
    var ids = (tree.categories || {}).lumo || [];
    var cat = tree.catalog || {};
    if (!ids.length || !cart || !cart.item_count) return null;

    var idx = window.TossitOffers.indexCart(cart);
    var hasLamp = ids.some(function (id) { return idx.products[Number(id)]; });
    if (hasLamp) return null;

    var dism = window.TossitOffers.dismissed();
    var candidates = ids.filter(function (id) {
      var p = cat[id];
      if (!p || !p.price) return false;
      if (dism.indexOf(String(id)) > -1) return false;
      // Never the lamp the engine just put on the strip. The shelf runs
      // after pick() and knew nothing about it, so an all-lamp-adjacent
      // cart could show the same night light twice under two headings.
      if (onStrip && onStrip[String(id)]) return false;
      return (p.variants || []).some(function (v) { return v.available; });
    });
    if (!candidates.length) return null;

    // Seeded like everything else: stable for this cart, different for others.
    var s = String((cart.items[0] && cart.items[0].product_id) || '');
    var n = 0;
    for (var i = 0; i < s.length; i++) n = (n * 31 + s.charCodeAt(i)) >>> 0;
    var p = cat[candidates[n % candidates.length]];

    return {
      id: p.variantId, productId: candidates[n % candidates.length],
      title: p.title, price: p.price, compareAt: p.compareAt,
      image: p.image, url: p.url
    };
  }

  function oneAwayNudge() {
    var b = (CFG.upsell && CFG.upsell.bundle) || null;
    if (!b || !b.percent || !(b.minGets >= 2)) return null;
    var sup = (CFG.upsell && CFG.upsell.supersedeAt) || 0;
    if (sup > 0 && rewardBasis() >= sup) return null;

    var baseIds = catIdSet([b.base]);
    var getIds = catIdSet(b.gets || []);
    var baseQty = 0, getsQty = 0;
    cart.items.forEach(function (i) {
      if (baseIds[String(i.product_id)]) baseQty += i.quantity;
      if (getIds[String(i.product_id)]) getsQty += i.quantity;
    });
    if (baseQty < 1 || getsQty !== b.minGets - 1) return null;

    return {
      percent: b.percent,
      noun: b.minGets === 2 ? 'both' : 'all ' + b.minGets,
      getIds: getIds
    };
  }

  function inCart(variantId) {
    return cart.items.some(function (i) { return i.variant_id === Number(variantId); });
  }

  /*
     Pick the offer that suits THIS cart, in the merchant's priority order.

     An offer is skipped when the customer already has its product, or when
     the cart contains anything from the collection it was told to stand
     down for. That second rule is what makes the push relevant rather than
     generic: whoever is buying darts is not shown more darts, and - the
     case that actually costs money - whoever is buying only a poster can be
     shown the darts that every poster description warns are not included.

     Product ids, not variant ids, for the exclusion test: someone holding
     the Red + Cyan starter pack should still count as having a starter pack
     when the offer is the Blue + Yellow one.
  */
  /*
     Which products to offer, delegated to the shared engine.

     assets/tossit-offers.js is the single brain behind every upsell surface -
     this drawer and the product-page box both call it. That is what lets the
     two escalate instead of repeat: the box pitches the complementary item,
     and by the time the drawer opens that product is in the cart, so the
     engine has already moved on to the next category. It also honours a
     dismissal made on either surface, so closing an offer on the product page
     stops the cart re-pitching it ninety seconds later.

     The hand-picked slots in theme settings stay as a fallback for anything
     the tree has no opinion about.
  */
  function pickOffers(limit, excludeProductId) {
    /*
       An EMPTY cart still gets suggestions.

       It used to return nothing, which made the emptiest, least useful state
       in the whole store into a dead end: a panel that said there was nothing
       in the cart and gave no way out of it except closing it. Someone who
       opened the cart is shopping - that is the worst possible moment to show
       them a blank box.

       Nothing else changes. The engine already handles an empty cart
       correctly: no categories are present, so the fallback rule fires and
       returns the catalogue's own suggestions. It just needs to be allowed to
       run, and the catalogue fill is left on so the drawer is never bare.
    */
    var empty = !cart.item_count;

    var picked = [];

    if (window.TossitOffers) {
      var preTaken = null;
      if (excludeProductId) { preTaken = {}; preTaken[String(excludeProductId)] = true; }
      picked = window.TossitOffers.pick({
        // No page anchor in a cart: bundle order is a product-page idea.
        bundleFirst: false,
        preTaken: preTaken,
        tree: CFG.upsell,
        cart: cart,
        limit: limit,
        /*
           Always allowed to fill from the catalog, not only on an empty
           cart. The sweep found the merchant-offer fallback this used to
           lean on had been dead since launch (a schema/snippet name
           mismatch meant it always emitted []), so non-empty carts often
           showed ONE card where two were designed. The engine's own
           guards - category-once, the band, dismissals, the zero-price
           gate - already prevent every failure mode the restriction was
           guarding against, and the fill self-heals as the catalog grows.
        */
        fillFromCatalog: true,
        seed: (cart.items && cart.items[0] && cart.items[0].product_id) || 0,
        fallbackHeading: empty ? 'Fan favorites — start here' : undefined,
        // Buyer intent, when the device has any: re-orders the last-resort
        // fill toward what this shopper actually browses. Never overrides
        // a rule, the band, or the cap - see tossit-intent.js.
        intent: window.TossitIntent ? window.TossitIntent.profile() : null
      });
    }
    return picked;
  }

  // Both cards, when both apply.
  //
  // These were an if/else, so the upsell disappeared at exactly the moment
  // the free gift appeared - the one point in the cart where the customer is
  // most willing to add something. AMP showed the reward and the offer
  // together and it was right to. They are different propositions: one is
  // owed to the customer, the other is being sold to them.
  /*
     An all-LUMO cart gets a different pitch entirely.

     The offers engine classifies by TOSSIT collections and knows nothing
     about lamps — left alone it offered darts to lamp buyers (the exit
     popup shipped the same bug). Instead: a scrollable rail of the other
     friends, tap to add, riding the existing [data-tcart-add] plumbing.
     Under four lamps the heading sells the sleepover; at four or more it
     just offers company. An earned gift card still shows above the rail —
     rewards are store-wide and owed.
  */
  /*
     The claimed mystery poster must not change what brand the cart IS.

     It is a $0 TOSSIT-vendor product, so the moment a lamp buyer pressed
     ADD on their earned reward, the vendor test failed, the friends rail
     vanished, and the engine pitched a TOSSIT poster at a lamp cart -
     the exact off-brand pitch the rail exists to prevent, triggered by
     accepting a reward. Rewards are brand-neutral: zero-priced lines and
     the configured gift are excluded before the test.
  */
  function cartIsAllLumo() {
    var items = ((cart && cart.items) || []).filter(function (i) {
      if (i.final_line_price === 0) return false;
      if (CFG.gift && i.variant_id === CFG.gift.id) return false;
      return true;
    });
    if (!items.length) return false;
    return items.every(function (i) {
      return String(i.vendor || '').toLowerCase() === 'live sogood';
    });
  }

  function renderLumoRail() {
    var have = {};
    cart.items.forEach(function (i) { have[i.variant_id] = true; });
    var lamps = (CFG.lumo || []).filter(function (l) { return !have[l.id]; });
    if (!lamps.length) return null;

    // Lamps only - the claimed $0 gift is not a friend at the sleepover
    // and must not skew the fourth-free heading threshold.
    var lampQty = 0;
    cart.items.forEach(function (i) {
      if (i.final_line_price === 0) return;
      if (String(i.vendor || '').toLowerCase() !== 'live sogood') return;
      lampQty += i.quantity || 0;
    });

    var picksHtml = lamps.map(function (l) {
      return (
        '<button class="tcart__lumo-pick" type="button" data-tcart-add="' + esc(l.id) + '" aria-label="Add ' + esc(l.title) + '">' +
          '<img src="' + esc(l.image) + '" alt="" width="56" height="56" loading="lazy">' +
          '<span class="tcart__lumo-name">' + esc(l.name) + '</span>' +
          '<span class="tcart__lumo-price">' + esc(money(l.price)) + '</span>' +
        '</button>'
      );
    }).join('');

    return (
      '<div class="tcart__promo tcart__lumo">' +
        '<p class="tcart__promo-head">' +
          (lampQty < 4 ? 'Add another friend — the 4th sleeps over free' : 'Add another friend') +
        '</p>' +
        '<div class="tcart__lumo-rail">' + picksHtml + '</div>' +
      '</div>'
    );
  }

  function renderOffer() {
    /*
       Wholesale buys from a catalogue. On a company session the whole offer
       strip stands down - no gift (already null server-side), no upgrade, no
       gap card, no engine picks, no lamp rail. One flag from the config, so
       this cannot drift from the Liquid-side gates.
    */
    if (CFG.b2b) {
      els.offer.hidden = true;
      els.offer.innerHTML = '';
      return;
    }
    var cards = [];

    var g = CFG.gift;
    var t = giftTier();
    if (g && t && rewardBasis() >= t.amount && !inCart(g.id)) {
      cards.push(
        '<div class="tcart__promo tcart__promo--gift">' +
          '<div class="tcart__claim">' +
            // The item box. A CSS cube spinning on the Mario Kart axis -
            // the photo IS the spoiler, so the prize spins instead of
            // showing itself. Six faces, four question marks; the locked
            // card below keeps the still grey tile, because the spin is
            // the celebration and it is not earned yet.
            '<div class="tcart__claim-img tcart__cubebox" aria-hidden="true">' +
              '<span class="tcart__cube"><i>?</i><i>?</i><i>?</i><i>?</i><i></i><i></i></span>' +
            '</div>' +
            '<div class="tcart__claim-body">' +
              '<p class="tcart__claim-kicker">You unlocked this</p>' +
              '<p class="tcart__claim-title">' + esc(g.title) + '</p>' +
              // The strike-through is the compare-at, not the price - the
              // price IS zero now (the gift is a $0 product, not a
              // discounted one), and a struck-out $0.00 reads as a bug.
              '<p class="tcart__claim-price">' +
                (g.compareAt > 0 ? '<s>' + esc(money(g.compareAt)) + '</s> ' : '') +
                '<b>Free</b></p>' +
            '</div>' +
            '<button class="tcart__claim-btn" type="button" data-tcart-add="' + esc(g.id) + '">Claim</button>' +
          '</div>' +
        '</div>'
      );
    }

    if (cartIsAllLumo()) {
      var rail = renderLumoRail();
      if (rail) cards.push(rail);
      els.offer.hidden = cards.length === 0;
      els.offer.innerHTML = cards.join('');
      return;
    }

    /*
       The prize, shown BEFORE it is earned.

       People spend toward a thing they have mentally picked out, not toward
       an abstract threshold - so below the gift tier the drawer shows the
       actual poster, greyed, with the exact distance left. No Add button:
       it cannot be claimed yet, and pretending otherwise is how trust dies.
       The moment the tier is crossed this card is replaced by the real
       claim card above, which is the payoff of having watched it approach.
       Everything on it restates config Shopify enforces - the tier amount,
       the gift, the price - so it can never promise what the server will
       not grant.
    */
    var locked = false;
    if (g && t && !inCart(g.id) && rewardBasis() > 0 && rewardBasis() < t.amount) {
      locked = true;
      cards.push(
        '<div class="tcart__promo tcart__promo--locked">' +
          '<div class="tcart__claim">' +
            // The same box, unlit and still. Lighting up IS the reward.
            '<div class="tcart__claim-img tcart__cubebox" aria-hidden="true">' +
              '<span class="tcart__cube"><i>?</i><i>?</i><i>?</i><i>?</i><i></i><i></i></span>' +
            '</div>' +
            '<div class="tcart__claim-body">' +
              '<p class="tcart__claim-kicker">' + esc(money(t.amount - rewardBasis())) + ' away from yours</p>' +
              '<p class="tcart__claim-title">' + esc(g.title) + '</p>' +
              '<p class="tcart__claim-price">' +
                (g.compareAt > 0 ? '<s>' + esc(money(g.compareAt)) + '</s> ' : '') +
                '<b>Free at ' + esc(money(t.amount)) + '</b></p>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }

    // The gap-filler leads when one exists - it is the card computed from
    // this exact cart - and it spends one of the offer slots rather than
    // adding a third. The locked prize spends one too, so the drawer never
    // grows a wall of pitches: prize + path is the whole story.
    var gap = tierGapOffer();

    /*
       The upgrade outranks everything below the gift. It transforms the
       order rather than adding to it - the largest single jump this cart
       can make - so it takes a slot ahead of the gap card and the engine's
       picks, and its target is handed to the engine as already-taken so
       the same pack cannot appear twice in one strip.
    */
    var upgrade = findUpgrade();
    if (upgrade) {
      /*
         Say what the upgrade UNLOCKS, not just what it costs.

         On the store's most common cart - one Starter - both paths clear
         $50: the gap card's $16.99 expansion lands $51.98, this card's
         $25.00 step lands $59.99. Only the gap card said so, so the strip
         attached its strongest true motivator to the cheaper path and
         steered the cart to the smaller total. The tier line is computed
         from the projected basis exactly as the product box computes its
         forecast, and appears only when the upgrade genuinely crosses a
         tier the cart has not already earned.
      */
      var unlocks = null;
      var tiersUp = (CFG.tiers || []).slice().sort(function (a, b) { return a.amount - b.amount; });
      var basisUp = rewardBasis();
      var projUp = basisUp - (upgrade.from.price * upgrade.from.quantity) +
                   (upgrade.to.price * upgrade.from.quantity);
      for (var ui = 0; ui < tiersUp.length; ui++) {
        var tu = tiersUp[ui];
        if (tu.amount > basisUp && tu.amount <= projUp) unlocks = tu;
      }
      var vc = null;
      if (upgrade.fromPlayers && upgrade.toPlayers && upgrade.toPlayers > upgrade.fromPlayers) {
        var needed = Math.ceil(upgrade.toPlayers / upgrade.fromPlayers);
        var multi = needed * upgrade.from.price;
        if (needed >= 2 && multi > upgrade.to.price) {
          vc = { needed: needed, saving: multi - upgrade.to.price };
        }
      }
      cards.push(
        '<div class="tcart__promo tcart__promo--upgrade">' +
          '<div class="tcart__claim">' +
            '<img class="tcart__claim-img" src="' + esc(upgrade.to.image) + '" alt="" width="56" height="56" loading="lazy">' +
            '<div class="tcart__claim-body">' +
              '<p class="tcart__claim-kicker">Upgrade your pack</p>' +
              '<p class="tcart__claim-title">' + esc(upgrade.to.title) + '</p>' +
              '<p class="tcart__claim-price">' +
                '<b>' + upgrade.toPlayers + ' players instead of ' + upgrade.fromPlayers + '</b>' +
                ' &middot; ' + esc(money(upgrade.diff)) + ' more' +
                (vc ? '<span class="tcart__upgrade-case">Beats buying ' + vc.needed + ' &mdash; save ' + esc(money(vc.saving)) + '</span>' : '') +
                (unlocks ? '<span class="tcart__upgrade-unlock">Lands at ' + esc(money(projUp)) +
                  ' &mdash; ' + esc(unlocks.label) + ' unlocked</span>' : '') +
              '</p>' +
            '</div>' +
            '<button class="tcart__claim-btn" type="button" ' +
              'data-tcart-upgrade="' + esc(upgrade.to.variantId) + '|' + esc(upgrade.from.key) + '|' + upgrade.from.quantity + '" ' +
              'aria-label="Upgrade ' + esc(upgrade.from.product_title) + ' to ' + esc(upgrade.to.title) + '">Upgrade</button>' +
          '</div>' +
        '</div>'
      );
    }

    // slots can be 0, and the engine reads a limit of 0 as "use the default"
    // (`o.limit || 2`), so 0 must never reach it - skip the call instead.
    var slots = Math.max(0, OFFER_COUNT - (gap ? 1 : 0) - (locked ? 1 : 0) - (upgrade ? 1 : 0));
    var picked = slots > 0 ? pickOffers(slots, upgrade ? upgrade.toProductId : null) : [];
    if (gap) {
      var already = null;
      picked.forEach(function (o) { if (String(o.productId) === String(gap.productId)) already = o; });
      if (already) {
        // Same product, stronger sentence: the tier line outranks the rule
        // line whenever both point at one card.
        already.heading = gap.heading;
      } else {
        picked.unshift(gap);
      }
    }

    /*
       The one-away nudge outranks every other heading on a card it fits.
       "One more add-on = 27% off both" is worth more to this exact cart
       than any generic pitch, and it may only be said on a card whose
       product actually counts toward the bundle.
    */
    var nudge = oneAwayNudge();
    if (nudge) {
      for (var ni = 0; ni < picked.length; ni++) {
        if (nudge.getIds[String(picked[ni].productId)]) {
          picked[ni].heading = 'One more add-on = ' + nudge.percent + '% off ' + nudge.noun;
          break;
        }
      }
    }

    picked.forEach(function (o) {
      var save = o.compareAt > o.price
        ? '<span class="tcart__offer-save">' + Math.round((1 - o.price / o.compareAt) * 100) + '% off</span>'
        : '';
      cards.push(
        '<div class="tcart__promo">' +
          (o.heading ? '<p class="tcart__promo-head">' + esc(o.heading) + '</p>' : '') +
          '<div class="tcart__claim">' +
            // Image and title link back to the product - reported missing
            // from the drawer while the cart page already had it. The Add
            // button keeps its own click; the rest of the card answers the
            // older instinct of tapping the thing itself.
            (o.url
              ? '<a class="tcart__claim-imglink" href="' + esc(o.url) + '" tabindex="-1" aria-hidden="true">' +
                  '<img class="tcart__claim-img" src="' + esc(o.image) + '" alt="" width="56" height="56" loading="lazy">' +
                '</a>'
              : '<img class="tcart__claim-img" src="' + esc(o.image) + '" alt="" width="56" height="56" loading="lazy">') +
            '<div class="tcart__claim-body">' +
              (o.url
                ? '<p class="tcart__claim-title"><a class="tcart__line-link" href="' + esc(o.url) + '">' + esc(o.title) + '</a></p>'
                : '<p class="tcart__claim-title">' + esc(o.title) + '</p>') +
              '<p class="tcart__claim-price">' +
                (o.compareAt > o.price ? '<s>' + esc(money(o.compareAt)) + '</s> ' : '') +
                '<b>' + esc(money(o.price)) + '</b> ' + save +
              '</p>' +
            '</div>' +
            '<button class="tcart__claim-btn" type="button" data-tcart-add="' + esc(o.id) + '">Add</button>' +
          '</div>' +
        '</div>'
      );
    });

    /*
       The lamp shelf closes the strip - always last, always under its own
       title, never mixed into the play offers above it.
    */
    var onStrip = {};
    picked.forEach(function (o) { onStrip[String(o.productId)] = true; });
    var lamp = lumoCrossSell(onStrip);
    if (lamp) {
      cards.push(
        '<div class="tcart__promo tcart__promo--lumo">' +
          '<p class="tcart__promo-head">For after the game</p>' +
          '<div class="tcart__claim">' +
            '<img class="tcart__claim-img" src="' + esc(lamp.image) + '" alt="" width="56" height="56" loading="lazy">' +
            '<div class="tcart__claim-body">' +
              '<p class="tcart__claim-kicker">LUMO night lights</p>' +
              '<p class="tcart__claim-title"><a class="tcart__line-link" href="' + esc(lamp.url) + '">' + esc(lamp.title) + '</a></p>' +
              '<p class="tcart__claim-price">' +
                (lamp.compareAt > lamp.price ? '<s>' + esc(money(lamp.compareAt)) + '</s> ' : '') +
                '<b>' + esc(money(lamp.price)) + '</b>' +
              '</p>' +
            '</div>' +
            '<button class="tcart__claim-btn" type="button" data-tcart-add="' + esc(lamp.id) + '" data-tcart-tag="cart-lumo">Add</button>' +
          '</div>' +
        '</div>'
      );
    }

    els.offer.hidden = cards.length === 0;
    els.offer.innerHTML = cards.join('');
  }

  // §5 — lines and totals

  function renderLines() {
    if (!cart.items.length) {
      els.lines.innerHTML = '';
      els.empty.hidden = false;
      els.foot.hidden = true;
      return;
    }
    els.empty.hidden = true;
    els.foot.hidden = false;

    nextDiscount = {};

    els.lines.innerHTML = cart.items.map(function (item, i) {
      // A line can be free two ways now: a discount zeroed it, or it IS a
      // zero-priced product (the mystery poster). Both must read "Free" -
      // "$0.00" on the reward line looks like a glitch, not a gift.
      var free = item.final_line_price === 0;
      var discounted = item.final_line_price < item.original_line_price;

      /*
         Animate the old price only on the render where a discount actually
         lands - not on every repaint, and not on a line that arrives already
         discounted. A line the customer has been looking at, that suddenly
         gains a struck-through price, is the only case worth marking.

         Keyed on item.key, Shopify's stable line identifier, so reordering
         or a quantity change on another line cannot mistrigger it.
      */
      var isGift = !!(CFG.gift && Number(item.variant_id) === Number(CFG.gift.id));
      var wasKnown = previousDiscount.hasOwnProperty(item.key);
      var struck = discounted && wasKnown && previousDiscount[item.key] === false;
      nextDiscount[item.key] = discounted;
      var variant = item.variant_title && item.variant_title !== 'Default Title'
        ? '<p class="tcart__line-variant">' + esc(item.variant_title) + '</p>' : '';
      /*
         Only tag a line the discount actually PAID something on. Shopify
         attaches zero-amount allocations to every line a Buy X Get Y merely
         considered - measured live: the full-price starter that qualified a
         bundle carried "27 Off Bundle -$0.00". Badging that line "27 OFF
         BUNDLE" while it pays list price is how a customer decides the cart
         is lying to them.
      */
      var discountTags = (item.line_level_discount_allocations || []).filter(function (d) {
        return d.amount > 0;
      }).map(function (d) {
        return '<span class="tcart__line-tag">' + esc(d.discount_application.title) + '</span>';
      }).join('');

      /*
         The was-price, from the truest source that has one:
         1. A discount that landed on THIS line - strike what the customer
            would have paid. Cart truth, as before.
         2. Otherwise the variant's compare-at, read from the offers catalog
            the drawer already ships. The PDP shows "$34.99 was $44.99" and
            the cart used to drop the was - the B2B team flagged it, and they
            are right that the saving should survive into the cart.
         3. The gift's own compare-at, so the earned reward reads
            "$8.99 Free" instead of a bare Free that undersells the prize.
         Guarded so a compare-at at or below the selling price never renders -
         the "Save $-19.99" class of bug stays impossible.
      */
      var wasCents = 0;
      if (discounted) {
        wasCents = item.original_line_price;
      } else {
        var cmp = 0;
        if (isGift && CFG.gift && CFG.gift.compareAt) cmp = CFG.gift.compareAt;
        else {
          var catRow = (CFG.upsell && CFG.upsell.catalog) ? CFG.upsell.catalog[String(item.product_id)] : null;
          if (catRow && catRow.compareAt) cmp = catRow.compareAt;
        }
        if (cmp > 0 && cmp * item.quantity > item.final_line_price) wasCents = cmp * item.quantity;
      }
      var priceNow = free ? 'Free' : esc(money(item.final_line_price));
      var price = wasCents > 0
        ? '<s>' + esc(money(wasCents)) + '</s> <b>' + priceNow + '</b>'
        : '<b>' + priceNow + '</b>';

      return (
        '<li class="tcart__line' + (free ? ' is-free' : '') + (struck ? ' is-marked-down' : '') +
          (arrivingGift && CFG.gift && Number(item.variant_id) === Number(CFG.gift.id) ? ' is-arriving' : '') + '">' +
          '<img class="tcart__line-img" src="' + esc(item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') : '') + '" alt="" width="60" height="60" loading="lazy">' +
          '<div class="tcart__line-body">' +
            /*
               The title links back to the product. item.url comes from
               /cart.js already carrying ?variant=, so it lands on the exact
               colour in the cart rather than the product's default - someone
               checking the Blue + Yellow they picked should not arrive at Red
               + Cyan.

               Title only, not the image as well: two links to one place is
               two tab stops and two things for a screen reader to read out,
               for no gain. Falls back to a plain <p> if a line ever arrives
               without a url, so a missing field can never produce a dead
               <a href="">.
            */
            (item.url
              ? '<p class="tcart__line-title"><a class="tcart__line-link" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a></p>'
              : '<p class="tcart__line-title">' + esc(item.product_title) + '</p>') +
            variant +
            (discountTags ? '<p class="tcart__line-tags">' + discountTags + '</p>' : '') +
            '<div class="tcart__line-bottom">' +
              /*
                 A reward has no quantity. The gift line used to carry the
                 same stepper as a purchase, so "+" handed out a second free
                 poster, and a third - seen live at quantity 2. It is one
                 prize, earned once; the X still removes it.
              */
              (isGift
                ? '<span class="tcart__qty tcart__qty--fixed">Reward</span>'
                : '<div class="tcart__qty">' +
                // Labelled with the product, because "Decrease quantity" on
                // every row tells a screen-reader user which button they are
                // on but never which of six things it changes.
                '<button type="button" class="tcart__qty-btn" data-tcart-qty="' + esc(item.key) + '" data-to="' + (item.quantity - 1) + '" aria-label="Decrease quantity of ' + esc(item.product_title) + '">&minus;</button>' +
                '<span class="tcart__qty-n">' + item.quantity + '</span>' +
                '<button type="button" class="tcart__qty-btn" data-tcart-qty="' + esc(item.key) + '" data-to="' + (item.quantity + 1) + '" aria-label="Increase quantity of ' + esc(item.product_title) + '">+</button>' +
              '</div>') +
              '<p class="tcart__line-price">' + price + '</p>' +
            '</div>' +
          '</div>' +
          '<button type="button" class="tcart__line-remove" data-tcart-qty="' + esc(item.key) + '" data-to="0" aria-label="Remove ' + esc(item.product_title) + '">' +
            '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M3 3 L13 13 M13 3 L3 13" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</li>'
      );
    }).join('');

    // One flight only. Cleared after the render that used it, so a later
    // quantity change on the reward line does not replay the celebration.
    arrivingGift = false;

    previousDiscount = nextDiscount;
  }

  function renderTotals() {
    var rows = '';
    var shipTier = tiers.filter(function (t) { return t.kind === 'shipping'; })[0];
    if (shipTier) {
      rows += '<div class="tcart__row"><dt>Shipping</dt><dd>' +
        (rewardBasis() >= shipTier.amount ? '<span class="tcart__free">Free</span>' : 'Calculated at checkout') +
        '</dd></div>';
    }
    if (cart.total_discount > 0) {
      rows += '<div class="tcart__row tcart__row--save"><dt>Discounts</dt><dd>&minus;' + esc(money(cart.total_discount)) + '</dd></div>';
    }
    /*
       The "was" price beside the subtotal, when discounts have moved it.

       Sourced from cart.original_total_price - Shopify's own pre-discount
       total for exactly this cart - and NOTHING else. Summing compare-at
       prices here would advertise "savings" the register never gave, which
       is the one lie this drawer must never tell. When no discount applies
       the two figures are equal and the strike-through simply is not there.
    */
    var was = cart.original_total_price > cart.total_price
      ? '<s class="tcart__was">' + esc(money(cart.original_total_price)) + '</s> '
      : '';
    rows += '<div class="tcart__row tcart__row--total"><dt>Subtotal</dt><dd>' + was + esc(money(cart.total_price)) + '</dd></div>';
    els.totals.innerHTML = rows;

    /*
       Light it. The class is removed when the animation ends rather than on
       a timer, so it cannot be left on by a re-render mid-burn - and it is
       removed BEFORE being re-added on a repeat crossing, so the browser
       restarts the animation instead of ignoring a class it already has.
    */
    if (ignite) {
      els.totals.classList.remove('is-ignited');
      void els.totals.offsetWidth;          // reflow, so the restart takes
      els.totals.classList.add('is-ignited');
      ignite = false;
      els.totals.addEventListener('animationend', function done(e) {
        if (e.target !== els.totals) return;
        els.totals.classList.remove('is-ignited');
        els.totals.removeEventListener('animationend', done);
      });
    }
  }

  /*
     The reward is revoked by the THEME, because nothing else can.

     The mystery poster is a $0 product, not a discount - which is what
     lets it stack with everything, and also means Shopify will happily
     leave it in a cart that no longer earns it. The old 75FreePoster
     discount would simply stop applying when the subtotal fell; a $0
     product has no such conscience. So on every render: if the gift is in
     the cart and the basis is under the tier, the gift comes out, with a
     message saying exactly why. The claim button already refuses to appear
     below the tier, so this is the matching half of the same promise.

     change() is busy-guarded and re-renders on completion, and once the
     line is gone this check cannot fire again - no loop. The one raw hole
     left is someone posting the variant id straight at /cart/add.js and
     checking out before any render runs; that is a determined person
     stealing one poster, and the server-side fix (a Shopify Function) is
     not worth its weight today.
  */
  /* Bounded too, for the case where change() keeps failing while getCart
     succeeds - the 429 that only rejects writes. Reset by any mutation
     that lands, so a genuine later revoke still runs. */
  var giftFixTries = 0;

  // Set the instant Claim is pressed, consumed by the next renderLines so
  // the reward's own line arrives animated - once, then cleared.
  var arrivingGift = false;

  function enforceGift() {
    var g = CFG.gift, t = giftTier();
    if (!g || !t || !cart || busy) return false;

    var line = null;
    for (var i = 0; i < cart.items.length; i++) {
      if (Number(cart.items[i].variant_id) === Number(g.id)) { line = cart.items[i]; break; }
    }
    if (!line) return false;

    /*
       ONE prize. The stepper is gone from the gift line now, but a cart can
       still arrive holding more than one - a second tab, the /cart page, a
       stale session, or the carts that exist from before this shipped.
       Anything above one is trimmed back rather than removed: they earned
       one, and taking the whole thing away would punish the wrong person.
    */
    if (giftFixTries >= 3) return false;

    if (line.quantity > 1) {
      giftFixTries++;
      change(line.key, 1);
      return true;
    }

    if (rewardBasis() >= t.amount) return false;
    giftFixTries++;
    // The message rides AFTER the removal resolves - the removal's own
    // completion pass runs watchDiscount, which resets the message node,
    // and a message posted first was measured being wiped by it.
    change(line.key, 0).then(function () {
      say('Your cart dropped under ' + money(t.amount) + ', so the mystery poster came out. Add a bit more to earn it back.');
    });
    return true;
  }

  function render() {
    if (!cart) return;
    if (enforceGift()) return; // change() re-renders when the removal lands
    els.count.textContent = cart.item_count;
    renderRewards();
    renderLines();
    renderOffer();
    renderTotals();
  }

  // §6 — discount codes
  //
  // The Ajax cart API cannot apply a code. /discount/<code> sets the cookie
  // and 200s (verified live), after which /cart.js reports it under
  // discount_codes. That field's own `applicable` flag proved unreliable -
  // it read false on a code whose discount was visibly applied to the lines -
  // so success is judged on whether the cart's money actually moved.

  function applyDiscount(code) {
    var before = cart.total_discount;
    els.discountMsg.hidden = true;
    return fetch('/discount/' + encodeURIComponent(code), { method: 'GET' })
      .then(function () { return getCart(); })
      .then(function (c) {
        cart = c;
        render();

        // Money moving is proof of success, but its absence is NOT proof of
        // failure. /cart.js describes items only - it has no shipping - so a
        // free-shipping code allocates nothing and can never move
        // total_discount. Judging on that alone told shoppers a perfectly
        // valid code "will not apply", while it sat attached and applied at
        // checkout. So also accept the code being attached, and let the money
        // check decide only the wording.
        //
        // `applicable` is checked as a veto rather than as proof: it read
        // false on a code whose discount was visibly applied to the lines,
        // so it is trusted to reject but never to confirm.
        var moved = c.total_discount > before;
        var attached = (c.discount_codes || []).some(function (d) {
          return d.code && d.code.toUpperCase() === code.toUpperCase() && d.applicable !== false;
        });
        var ok = moved || attached;

        els.discountMsg.hidden = false;
        els.discountMsg.textContent = moved
          ? 'Code applied.'
          : ok
            ? 'Code applied — the saving shows at checkout.'
            : 'That code will not apply to this cart.';
        els.discountMsg.classList.toggle('is-bad', !ok);
        return c;
      })
      .catch(function () {
        els.discountMsg.hidden = false;
        els.discountMsg.textContent = 'Could not reach the discount service. Try again at checkout.';
        els.discountMsg.classList.add('is-bad');
      });
  }

  // §7 — open / close

  function open() {
    if (!root.hasAttribute('hidden')) return;
    lastFocus = document.activeElement;
    root.removeAttribute('hidden');

    // Forcing a reflow, not waiting a frame. The transition needs a start
    // state to move from, and requestAnimationFrame supplies one - but only
    // when frames are actually being produced. In a backgrounded or
    // zero-size tab rAF is throttled or never fires, and the drawer then
    // unhides without ever gaining .is-open: present in the DOM, still
    // translated fully off-screen, and impossible to close because it does
    // not look open. Reading offsetWidth flushes layout synchronously, so
    // the class always lands on the same tick as the unhide.
    void root.offsetWidth;
    root.classList.add('is-open');
    document.body.classList.add('tcart-locked');
    els.panel.focus();

    /*
       Tell the arbiter the drawer is up, so promos and cards get out of the
       way. This is the LAST statement on purpose: the cart is transactional
       and never asks permission, and nothing that can throw may sit between
       the customer and an open drawer. If the arbiter is missing or broken,
       the drawer has already opened exactly as before.
    */
    try {
      if (window.TossitOverlay) window.TossitOverlay.request('cart', {
        priority: 90, blocking: true, kind: 'transactional'
      });
    } catch (e) {}
    document.addEventListener('keydown', onKey, true);
  }

  function close() {
    if (root.hasAttribute('hidden')) return;
    root.classList.remove('is-open');
    try { if (window.TossitOverlay) window.TossitOverlay.release('cart'); } catch (e) {}
    document.body.classList.remove('tcart-locked');
    document.removeEventListener('keydown', onKey, true);
    var done = function () { root.setAttribute('hidden', ''); };
    // Match the CSS; falls back immediately when motion is reduced.
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else window.setTimeout(done, 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    var f = els.panel.querySelectorAll('a[href],button:not([disabled]),input,select,[tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // §8 — wiring

  root.addEventListener('click', function (e) {
    /*
       While a request is in flight, ignore clicks entirely.

       Without this the drawer CLOSED itself on any click during a request.
       .is-busy puts pointer-events:none on the panel, so the click missed
       every control and landed on the full-viewport scrim underneath, which
       carries data-tcart-close. Tapping "+" twice quickly - or any mobile
       double-tap - shut the cart mid-purchase. The panel being unclickable
       was intended; falling through to the one element that closes the
       drawer was not.
    */
    if (busy) { e.preventDefault(); return; }

    if (e.target.closest('[data-tcart-close]')) { close(); return; }

    /*
       Anything outside the panel closes, whether or not the scrim is there
       to be clicked.

       The scrim carries data-tcart-close and is meant to catch this, but it
       is an empty div and something outside the theme collapses empty divs
       to display:none — which turned the drawer into a trap: this listener
       is bound to the root, the root fills the viewport, so every outside
       click arrived here and matched nothing. The CSS now forces the scrim
       back, but that fix depends on out-competing a rule this theme cannot
       see. Testing "not in the panel" needs no element to exist at all, so
       the drawer stays closable even if that rule changes again.
    */
    if (!e.target.closest('.tcart__panel')) { close(); return; }

    if (e.target.closest('[data-tcart-undo]')) {
      if (!undoLine) return;
      var back = undoLine;
      undoLine = null;
      els.discountMsg.hidden = true;
      add(back.id, back.quantity);
      return;
    }

    // The key is a string, not a number - do not coerce it.
    var qty = e.target.closest('[data-tcart-qty]');
    if (qty) { change(qty.dataset.tcartQty, Number(qty.dataset.to)); return; }

    var upBtn = e.target.closest('[data-tcart-upgrade]');
    if (upBtn) {
      /*
         The swap is ADD THEN REMOVE, never the reverse - the popup's rule,
         kept: if the add fails the cart still has the original pack, and
         the remove only runs once the upgrade is really in. Quantity rides
         along (2 starters become 2 family packs), matching the popup
         exactly so the two surfaces cannot disagree about what an upgrade
         does.
      */
      var parts = upBtn.dataset.tcartUpgrade.split('|');
      var toId = parts[0], fromKey = parts[1], qty = Number(parts[2]) || 1;
      upBtn.disabled = true;
      if (window.TossitOffers && window.TossitOffers.attribute) {
        window.TossitOffers.attribute('cart-upgrade');
      }
      /*
         Remove the old pack ONLY once the new one is really in the cart.

         `if (!c)` could not detect a failure: add() resolves through
         release(), which returns the cart it was handed - and on the error
         path that is a RE-FETCHED cart, truthy, with the upgrade absent. So
         a failed add still ran the remove and the customer ended with less
         than they started. The proof is now the cart itself.
      */
      add(toId, qty).then(function (c) {
        var landed = c && (c.items || []).some(function (i) {
          return String(i.variant_id) === String(toId);
        });
        if (!landed) { upBtn.disabled = false; return null; }
        return change(fromKey, 0);
      });
      return;
    }

    var addBtn = e.target.closest('[data-tcart-add]');
    if (addBtn) {
      addBtn.disabled = true;
      /*
         The box bursts before the prize arrives.

         Pressing Claim used to swap one card for one line with nothing
         between - the most celebratory moment in the cart, rendered as a
         list update. Now the cube flies apart, and the line it becomes
         lands with a pop. The add is NOT delayed by it: the request goes
         out immediately and the burst plays over the top, so the theatre
         costs nothing if the network is slow. arrivingGift survives the
         re-render and tags the new line once.
      */
      var giftPress = CFG.gift && String(CFG.gift.id) === String(addBtn.dataset.tcartAdd);
      if (giftPress) {
        var card = addBtn.closest('.tcart__promo');
        if (card) card.classList.add('is-bursting');
        arrivingGift = true;
      }
      // Which surface sold it, recorded as a cart attribute for the weekly
      // read of what actually converts. The gift is tagged separately from
      // paid offers - a claimed reward is not a sale.
      if (window.TossitOffers && window.TossitOffers.attribute) {
        var isGift = CFG.gift && String(CFG.gift.id) === String(addBtn.dataset.tcartAdd);
        window.TossitOffers.attribute(
          addBtn.dataset.tcartTag || (isGift ? 'cart-gift' : 'cart-offer'));
      }
      add(addBtn.dataset.tcartAdd, 1).then(function () { addBtn.disabled = false; });
    }
  });

  /*
     Throw the dart on checkout.

     Deliberately does NOT preventDefault and does NOT delay the submit. The
     form goes immediately and the dart lands during the navigation that was
     already happening, so the flourish costs the customer exactly nothing on
     the one click where speed actually matters. If the page leaves before it
     finishes, fine.

     Guarded on reduced motion; the CSS keeps it off phones.
  */
  var checkoutBtn = root.querySelector('[data-tcart-checkout]');
  if (checkoutBtn && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    checkoutBtn.addEventListener('click', function () {
      if (checkoutBtn.classList.contains('th-atc-loading')) return;

      // The label must carry .th-atc-label for the keyframes to move it.
      var label = checkoutBtn.querySelector('.tcart__checkout-label');
      if (label && !label.classList.contains('th-atc-label')) {
        label.classList.add('th-atc-label');
      }
      if (!checkoutBtn.querySelector('.th-atc-stage')) {
        checkoutBtn.insertAdjacentHTML('beforeend', ATC_STAGE_HTML);
      }

      checkoutBtn.classList.add('th-atc-loading');
      // ALWAYS taken off again. The bespoke strike this replaces animated
      // forwards and was never cleared, so the dart finished its flight and
      // then simply stayed - parked through the middle of the word CHECKOUT
      // for the life of the page.
      window.setTimeout(function () {
        checkoutBtn.classList.remove('th-atc-loading');
      }, 3000);
    });
  }

  els.discountForm.addEventListener('submit', function (e) {
    e.preventDefault();
    var code = els.discountForm.discount.value.trim();
    if (code) applyDiscount(code);
  });

  /*
     The wallet tray's fade, told the truth about its own state.

     Pure CSS cannot ask "is this box overflowing", so an unconditional fade
     would sit over the last button even when there is nothing under it. Two
     classes instead: one for whether the tray overflows at all, one for
     whether it is already at the bottom. The fade needs both.

     A ResizeObserver rather than a check at render time, because these
     buttons are Shopify web components - they mount and take their height
     well after this script has finished, so anything measured up front would
     be measuring an empty box. Watching the inner grid as well catches a
     wallet that appears late; watching the tray catches the panel resizing
     around it.
  */
  /*
     NOTHING HERE REORDERS THE WALLETS, AND NOTHING SHOULD.

     There was an orderWallets() that moved the buttons into a house order
     (Shop Pay, Apple Pay, PayPal, Google Pay) by appendChild-ing the
     light-DOM children of Shopify's accelerated-checkout element, reapplied
     from a MutationObserver because Shopify mounts them late.

     It corrupted them. With three wallets it looked fine and tested clean;
     the moment a fourth (Amazon Pay) was switched on in admin, the live
     drawer rendered a half-width Amazon Pay button, a duplicated Google Pay
     button, and buttons overlapping each other with a neighbour's colour
     showing through. Each of those elements owns an iframe it initialises
     itself, and moving the node out from under that work while it is still
     in flight is what produced the mess - the observer fires on Shopify's own
     internal churn, so we were reliably moving them mid-render.

     Shopify offers no setting for wallet order and CSS cannot reach it
     either (the host is display: inline, so `order` is inert on its
     children). That is the whole option space: the order is Shopify's, or
     the buttons are broken. Cosmetic ordering is not worth a corrupted
     purchase path - leave them exactly where Shopify puts them.
  */

  if (els.walletScroll) {
    var syncTray = function () {
      var el = els.walletScroll;

      /*
         Decide FIRST whether this box is allowed to be a scrollport at all.

         Shopify's component chooses row or stack by measuring the box it is
         given, and a scroll container is not a neutral thing to measure
         inside. While this was unconditional the wallets rowed on desktop but
         stacked on an iPhone at a width where three of them plainly fit. So
         the box stays an ordinary block until the buttons have actually
         stacked, and only then may it shrink and scroll.

         Measured off the inner grid, whose height is its content either way -
         it is never the thing being clipped - so this cannot oscillate.
         One row is 46px plus 6px of headroom; 70 clears that without being
         near two rows.
      */
      var grid = el.firstElementChild;
      el.classList.toggle('is-tall', grid ? grid.scrollHeight > 70 : false);
    };

    /*
       No scroll listener, and no fade classes for it to drive.

       There used to be sticky gradients at both ends of this tray to signal
       that it scrolled. They worked against the thing that actually signals
       it: the half a wallet button left showing at the bottom. A gradient
       strong enough to read as a fade is strong enough to wash out the logo
       underneath it, and a half-erased PayPal mark says "broken styling", not
       "scroll me". The cut-off button is the affordance; it just has to be
       left legible.
    */

    if (window.ResizeObserver) {
      var trayWatch = new ResizeObserver(syncTray);
      trayWatch.observe(els.walletScroll);
      var trayGrid = els.walletScroll.firstElementChild;
      if (trayGrid) trayWatch.observe(trayGrid);
    } else {
      window.addEventListener('resize', syncTray);
    }

    /*
       No MutationObserver on this tray either.

       There was one, purely to reapply a wallet ordering that has now been
       removed (see the note above). It fired on Shopify's own internal churn
       - every iframe repaint - and that is exactly what made the reordering
       so destructive: it moved the buttons around while they were still
       initialising. With the ordering gone there is nothing left for it to
       do, and watching a subtree we no longer touch would only be a way to
       start touching it again.

       Sizing is the ResizeObserver's job above, and it is enough: it fires
       when the wallets mount and change the tray's height, which is the only
       thing syncTray() needs to know.
    */
    syncTray();
  }

  // The cart icon opens the drawer instead of navigating.
  document.addEventListener('click', function (e) {
    var iconLink = e.target.closest('#cart-icon-bubble');
    if (!iconLink) return;
    e.preventDefault();
    /*
       The catch is the whole point. preventDefault() has already cancelled
       the customer's navigation to /cart, so if getCart() rejects - one
       dropped request on a phone is enough - the promise died silently and
       the icon did LITERALLY NOTHING. Pressing it again did nothing again.
       The one control everybody uses to check their cart became inert with
       no error and no way back.

       So on failure, hand them the page they were going to anyway.
    */
    getCart()
      .then(function (c) { cart = c; render(); open(); })
      .catch(function () { window.location.href = CFG.cartUrl || '/cart'; });
  });

  // Any add-to-cart press anywhere: refresh and reveal. Uses click for the
  // same reason tossit-header.js does - a cart app can eat the submit event.
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest(
        'button[name="add"], button.product-form__submit, button.quick-add__submit, #tl-atc, #tlf-btn'
      );
      if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

      /*
         Wait for the count to actually change rather than guessing.

         This used to open once on a fixed 700ms timer with nothing to
         correct it. On any connection slower than that the drawer opened
         showing the cart WITHOUT the item just added, and never refreshed -
         so the customer, reasonably, added it again.

         Poll instead: open as soon as the server confirms the item, and give
         up after a few seconds. Opening on the last attempt regardless means
         a genuinely failed add still shows the real cart rather than
         nothing, and a cart whose count legitimately did not move (quantity
         already at its ceiling) still gets a drawer.
      */
      var before = cart ? cart.item_count : -1;
      var tries = 0;
      var poll = function () {
        tries++;
        getCart()
          .then(function (c) {
            cart = c;
            if (c.item_count !== before || tries >= 5) { render(); open(); }
            else window.setTimeout(poll, 600);
          })
          .catch(function () {
            // Never leave the press unanswered: show what we last knew.
            if (cart) { render(); open(); }
          });
      };
      window.setTimeout(poll, 450);
    },
    true
  );

  document.addEventListener('tossit:cart:open', function () {
    getCart().then(function (c) { cart = c; render(); open(); });
  });

  getCart().then(function (c) { cart = c; render(); });
})();
